import { useState, useEffect, useRef } from 'react'
import { Bell, BellRing } from 'lucide-react'
import { io } from 'socket.io-client'
import api from '../lib/api'
import { 
  requestNotificationPermission, 
  getNotificationPermission,
  showAlertNotification,
  showReminderNotification,
  showTenderDeadlineNotification
} from '../services/notificationService'
import toast from 'react-hot-toast'

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [showDropdown, setShowDropdown] = useState(false)
  const [permissionGranted, setPermissionGranted] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    // Check notification permission
    setPermissionGranted(getNotificationPermission() === 'granted')

    // Connect to WebSocket - use API URL or fallback to localhost
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000'
    const socketUrl = apiUrl.replace('/api', '').replace(/\/$/, '')
    const socket = io(socketUrl)

    socket.on('newTender', (data) => {
      addNotification('New high-priority tender discovered', 'tender')
      showAlertNotification({ 
        title: 'New Tender', 
        message: data?.title || 'A new high-priority tender has been discovered',
        priority: 'High'
      })
    })

    socket.on('urgentDeadline', (data) => {
      addNotification('Tender deadline approaching!', 'urgent')
      if (data) {
        showTenderDeadlineNotification(data)
      }
    })

    socket.on('followupDue', (data) => {
      addNotification('Follow-up reminder', 'info')
      showReminderNotification({
        title: 'Follow-up Reminder',
        message: data?.message || 'You have a follow-up due'
      })
    })

    socket.on('newAlert', (data) => {
      addNotification(data?.title || 'New Alert', 'alert')
      showAlertNotification(data)
    })

    socket.on('reminder', (data) => {
      addNotification(data?.title || 'Reminder', 'reminder')
      showReminderNotification(data)
    })

    return () => socket.disconnect()
  }, [])

  const addNotification = (message, type) => {
    const newNotif = { id: Date.now(), message, type, read: false }
    setNotifications(prev => [newNotif, ...prev].slice(0, 10))
    setUnreadCount(prev => prev + 1)
  }

  const markAsRead = () => {
    setUnreadCount(0)
    setNotifications(prev => prev.map(n => ({ ...n, read: true })))
  }

  const handleEnableNotifications = async () => {
    const granted = await requestNotificationPermission()
    setPermissionGranted(granted)
    if (granted) {
      toast.success('Push notifications enabled!')
    } else {
      toast.error('Notification permission denied')
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => {
          setShowDropdown(!showDropdown)
          if (!showDropdown) markAsRead()
        }}
        className="relative p-2.5 rounded-xl hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-all"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-pulse" />
        ) : (
          <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        )}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
            {!permissionGranted && (
              <button
                onClick={handleEnableNotifications}
                className="text-xs px-2 py-1 bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-400 rounded hover:bg-primary-200"
              >
                Enable Push
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">No notifications</p>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    !notif.read ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                  }`}
                >
                  <p className="text-sm text-gray-900 dark:text-gray-100">{notif.message}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Just now</p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell
